import { routes } from "../../lib/nav";
import { SITE_URL } from "../../lib/site";
import type { CaseStudy } from "./types";

const dir = "/cases/satoshi-friends";

const img = (file: string, alt: string, span: 2 | 3 | 6 = 6) => ({
  src: `${dir}/${file}.webp`,
  alt,
  width: 1920,
  height: 1200,
  wide: span === 6,
  span,
});

const mobile = (file: string, alt: string) => ({
  src: `${dir}/${file}.webp`,
  alt,
  width: 1920,
  height: 1200,
  span: 3 as const,
});

export const satoshiFriends: CaseStudy = {
  slug: "satoshi-friends",
  title: "Satoshi Friends",
  cardTitle: "Разработка сайта для онлайн-сообщества Satoshi Friends",
  subtitle: "Личный бренд, медиа и закрытое сообщество",
  industry: { label: "Медиа и личный бренд", slug: "media" },
  eyebrow: "Сайт · Онлайн-сообщество",
  h1: "Разработка сайта для сообщества Satoshi Friends",
  lead:
    "Разработали сайт Satoshi Friends: личный бренд основателя, медиа, закрытое сообщество, академия и переходы в продукты — на одной платформе с формами сбора Telegram и e-mail.",
  heroButtons: { site: "Смотреть сайт", discuss: "Обсудить сайт сообщества" },

  seo: {
    primaryKeyword: "разработка сайта для онлайн-сообщества",
    secondaryKeywords: [
      "сайт для сообщества разработка",
      "создание сайта комьюнити",
      "сайт личного бренда и сообщества",
      "разработка сайта криптопроекта",
    ],
    semanticEntities: [
      "личный бренд основателя",
      "закрытое сообщество",
      "академия",
      "медиакит",
      "магазин",
      "Telegram username",
      "e-mail форма",
    ],
    excludedKeywords: [
      "курс криптовалют",
      "купить биткоин",
      "крипто сигналы",
      "обучение трейдингу",
    ],
    title: "Сайт Satoshi Friends — кейс HAY.agency",
    description:
      "Кейс HAY.agency: сайт Satoshi Friends с личным брендом основателя, медиа, закрытым сообществом, академией и переходами в продукты.",
    h1: "Разработка сайта для сообщества Satoshi Friends",
    slug: "satoshi-friends",
    canonicalUrl: `${SITE_URL}/cases/satoshi-friends`,
    ogTitle: "Сайт онлайн-сообщества Satoshi Friends",
    ogDescription:
      "Личный бренд основателя, медиа, закрытое сообщество, академия и переходы в продукты на одной платформе.",
    ogImage: `${SITE_URL}${dir}/satoshi-friends-og.webp`,
    robotsIndex: true,
    datePublished: "2024-03-01",
    dateModified: "2026-09-01",
  },

  links: {
    relatedService: {
      label: "Разработка сайтов",
      to: routes.sites,
      note: "Медиа, сообщество и конверсионные формы",
    },
    relatedIndustry: {
      label: "Разработка сайтов для медиа и личного бренда",
      to: routes.sites,
      note: "Личный бренд, контент и сообщество",
    },
    relatedCases: [
      {
        label: "Разработка новостного сайта Satoshi News",
        to: "/cases/satoshi-news",
        note: "Медиа о криптовалютах с лентой и аналитикой",
      },
      {
        label: "Разработка сайта для галереи современного искусства Reportage",
        to: "/cases/reportage",
        note: "Editorial-подача и авторский контент",
      },
    ],
    relatedArticles: [],
  },

  cta: {
    title: "Давайте обсудим, как мы можем масштабировать ваш бизнес",
    text: "Оставьте заявку — свяжемся в течение двух часов, чтобы задать вопросы и обсудить задачу.",
    button: "Обсудить сайт сообщества",
  },

  client: {
    name: "Satoshi Friends",
    url: "https://satoshifriends.com/",
    city: "Онлайн",
    industry: "Онлайн-сообщество и медиа",
  },
  year: "2024",
  services: [
    "Дизайн сайта сообщества",
    "Разработка блока личного бренда",
    "Навигация между сообществом, академией и новостями",
    "Медиа, формы и Telegram-переходы",
  ],
  platform: "Custom",
  tags: [
    { label: "Корпоративный сайт", to: routes.sites },
    { label: "Разработка", to: routes.sites },
    { label: "Поддержка", to: routes.sites },
  ],
  facts: [
    { label: "Клиент", value: "Satoshi Friends" },
    { label: "Тип проекта", value: "Сайт онлайн-сообщества и личного бренда" },
    { label: "Платформа", value: "Custom" },
    { label: "Год", value: "2024" },
  ],

  results: {
    title: "Что реализовали",
    items: [
      "Сайт, связывающий сообщество, академию, новости и закрытый раздел",
      "Блок личного бренда основателя с историей и социальными доказательствами",
      "Медиакит и магазин с переходами в Telegram и соцсети",
      "Показ аудитории социальных платформ",
      "Формы сбора Telegram username и e-mail",
      "Сильная мобильная версия под входы из Telegram",
    ],
  },

  story: [
    {
      title: "Задача",
      paragraphs: [
        "Satoshi Friends объединяет личный бренд основателя, медиа, академию и закрытое сообщество в одном проекте. Нужен был сайт, который не сводит всё к одной ленте, а понятно проводит между продуктами и подводит к подписке в Telegram.",
        "Вторая часть задачи — мобильный сценарий: значимая часть входов связана с Telegram, поэтому мобильная версия должна работать как основной, а не второстепенный сценарий.",
      ],
    },
  ],
  features: [],

  design: {
    title: "Дизайн: тёмная digital-подложка без крипто-шаблонов",
    paragraphs: [
      "Интерфейс собран на тёмной digital-подложке без криптомонет и типовой blockchain-графики — акцент на личном бренде и контенте, а не на визуальных штампах отрасли.",
      "Навигация между сообществом, академией и новостями решена одной понятной схемой, а не отдельными несвязанными разделами.",
    ],
    bullets: [
      "Тёмная digital-подложка вместо шаблонной крипто-графики",
      "Единая схема перехода между продуктами",
      "Mobile-first логика под входы из Telegram",
    ],
  },

  identity: {
    fonts: [
      { name: "Inter", role: "Заголовки", sample: "Satoshi Friends" },
      { name: "Inter", role: "Интерфейс и текст", sample: "Закрытое сообщество" },
    ],
    colors: [
      { hex: "#14151A", name: "Фон" },
      { hex: "#F5C542", name: "Акцент бренда" },
      { hex: "#EDEDED", name: "Текст" },
    ],
  },

  cover: img(
    "satoshi-friends-case-cover",
    "Главная страница сайта онлайн-сообщества Satoshi Friends",
    6,
  ),

  gallery: [
    img("satoshi-friends-audience", "Блок личного бренда и аудитории Satoshi Friends", 6),
    img("satoshi-friends-products", "Схема перехода между продуктами Satoshi Friends", 6),
  ],

  responsive: {
    text: [
      "Мобильная версия построена как основной сценарий: hero, история и переход к сообществу и Telegram занимают первый экран.",
      "Формы и Telegram-переходы в мобильной версии показаны заполненными состояниями.",
    ],
    images: [
      mobile("satoshi-friends-mobile-hero", "Мобильная главная сайта Satoshi Friends"),
      mobile("satoshi-friends-mobile-form", "Мобильная форма и Telegram-переход Satoshi Friends"),
    ],
  },

  columnBlocks: [
    {
      id: "personal-brand",
      title: "Личный бренд",
      paragraphs: [
        "Hero, история основателя и социальные доказательства собраны без длинной биографии — только то, что подтверждает статус и приводит к следующему шагу.",
      ],
      bullets: ["Hero с историей основателя", "Социальные доказательства", "Аудитория социальных платформ"],
    },
    {
      id: "products",
      title: "Навигация между продуктами",
      paragraphs: [
        "Сообщество, академия, новости и закрытый раздел показаны одной схемой — пользователь понимает разницу продуктов ещё до перехода.",
      ],
      bullets: ["Сообщество", "Академия", "Новости и закрытый раздел"],
    },
    {
      id: "conversion",
      title: "Контент и конверсия",
      paragraphs: [
        "Медиа, форма подписки и Telegram-переход собраны вокруг ключевых CTA сайта.",
      ],
      bullets: ["Медиакит и магазин", "Форма Telegram username и e-mail", "Переходы в Telegram и соцсети"],
    },
  ],

  servicesDone: {
    title: "Комплекс работ HAY.agency",
    items: [
      { label: "Разработка сайта", to: routes.sites },
      { label: "Поддержка проекта", to: routes.sites },
    ],
  },
};
